import { PrinterOutlined } from '@ant-design/icons';
import { App, Button, Tooltip } from 'antd';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';
import { getLocale } from 'translations/locale';

import { useRestApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { RingComplianceStatus } from 'business/quality-control/pages/ring-compliance-report/hooks/data/types';
import config from 'config';
import { useRingValidationV1ControllerExportPdfs } from 'generated/apiComponents';
import { Module_Enum } from 'generated/graphql';
import { downloadFile } from 'technical/print';

export const PrintRingButton = ({
  ringStatus,
  ringNumber,
  isValidationActivated,
  hasPdf,
}: {
  ringStatus: string;
  ringNumber: number;
  isValidationActivated: boolean;
  hasPdf: boolean;
}) => {
  const { t } = useTranslation();
  const { currentConstructionSite } = useAppContext();
  invariant(currentConstructionSite, 'No construction site');

  const headers = useRestApiHeaders(Module_Enum.QualityControl);

  const { message } = App.useApp();
  const { mutateAsync: exportPdfs, isPending } =
    useRingValidationV1ControllerExportPdfs();

  const tooltipMessage = currentConstructionSite.isQualityValidatorActivated
    ? t('qualityControl.ringComplianceReport.printReportWarning')
    : t(
        'qualityControl.ringComplianceReport.printReportWarningWithoutValidation',
      );

  const canPrintRing =
    hasPdf &&
    ((isValidationActivated && ringStatus === RingComplianceStatus.Approved) ||
      (!isValidationActivated &&
        [
          RingComplianceStatus.Approved,
          RingComplianceStatus.Compliant,
        ].includes(ringStatus as RingComplianceStatus)));

  const handleClick = async () => {
    try {
      const result = await exportPdfs({
        body: {
          fromRingNumber: ringNumber,
          toRingNumber: ringNumber,
          locale: getLocale(),
        },
        pathParams: { constructionSiteId: currentConstructionSite.id },
        headers,
      });

      if (result.url) {
        const extension = result.url.endsWith('.zip') ? 'zip' : 'pdf';
        await downloadFile(
          `${config.gcp.publicUri}/${result.url}`,
          `${t('qualityControl.ringComplianceReport.printReportsModal.fileName')}-${ringNumber}.${extension}`,
        );
      }

      message.open({
        type: 'success',
        content: t(
          'qualityControl.ringComplianceReport.printReportsModal.success',
        ),
      });
    } catch {
      message.open({
        type: 'error',
        content: t(
          'qualityControl.ringComplianceReport.printReportsModal.error',
        ),
      });
    }
  };

  return (
    <Tooltip title={!canPrintRing ? tooltipMessage : undefined}>
      <span>
        <Button
          icon={<PrinterOutlined />}
          iconPosition="end"
          disabled={!canPrintRing}
          loading={isPending}
          onClick={handleClick}
        >
          {t('common.print')}
        </Button>
      </span>
    </Tooltip>
  );
};
